import { useTheme, alpha, Box } from "@mui/material";
import {
  CartesianGrid,
  XAxis,
  YAxis,
  Line,
  ResponsiveContainer,
  Bar,
  ComposedChart,
  Area,
  Legend,
  Tooltip,
  Cell,
  ReferenceLine,
  Label,
} from "recharts";
import React, { useMemo, useCallback, memo } from "react";
import { useXHeight, useYWidth } from "Hooks/useAnalyticsAxisDimensions";
import { ApiChartYAxisKey, ApiMetric, ApiYAxisChartType } from "@incendium/api";
import ChartLegendWrapper from "./ChartLegendWrapper";
import { displayDataMapFn, mergeComparisonData } from "../utils/utils";
import { formatDimension } from "../services/dimensionsFormatter";
import useFormatMetric from "../hooks/useFormatMetric";
import { xStandardProps, yStandardProps } from "../constants/constants";
import AnalyticsCardCustomTooltip from "./AnalyticsCardCustomTooltip";
import { useAnalyticsContext } from "Providers/AnalyticsProvider";
import { IStyledChartProps, TChartData } from "Interfaces";
import ChartModel from "../models/Chart.model";
import NoData from "Components/NoData";
import { formatNumber } from "Helpers/numbers";
import { truncate } from "Helpers/truncate";
import { DIMENSION_TRUNCATE } from "../constants";

function StyledComposedChart({
  data,
  children,
  yAxisProps,
  yAxisKeys,
  xAxisProps,
  height,
  aspect,
  noAnimation,
  noLegend,
  noReOrder,
  showTooltip,
  fills,
  showReferenceLineKey,
  tooltipProps,
  metricsOrder,
  layout = "horizontal",
  comparismentChart,
  dimensions,
  noAxis,
  verticalReferenceLines = [],
}: IStyledChartProps) {
  const formatMetric = useFormatMetric();
  // todo, this should be outside of this component
  const chartModel = useMemo(() => {
    return new ChartModel({
      yAxisKeys: yAxisKeys || [],
      attributes: [], // attributes not passed to this component, should be there when we move outside
    });
  }, [yAxisKeys]);

  const { colourMap } = useAnalyticsContext();
  const theme = useTheme();
  const isVertical = layout === "vertical";
  const xProps = {
    ...xStandardProps,
    ...(isVertical
      ? {
          angle: 0,
          textAnchor: "middle" as const,
          tickMargin: 6,
          tickCount: 6,
        }
      : {}),
    ...xAxisProps,
  };
  const xHeight = useXHeight(layout, data, yAxisKeys, xProps, yAxisProps);
  const yWidths = useYWidth(layout, data, yAxisKeys, xProps, yAxisProps);
  const hasRightAxis = Array.isArray(yAxisKeys) && yAxisKeys.length > 1;

  const chartDataKeys = useMemo(() => {
    return chartModel
      ? chartModel.getChartDataKeys(data, metricsOrder, noReOrder)
      : [];
  }, [data, noReOrder, metricsOrder, chartModel]);

  const numericDomain = useMemo(() => {
    return [
      (dataMin: number) => (dataMin < 0 ? dataMin * 1.2 : 0),
      (dataMax: number) => (dataMax === 0 ? 1 : dataMax * 1.2),
    ] as [(min: number) => number, (max: number) => number];
  }, []);

  const formatCategoryTick = useCallback(
    (value: string) => {
      const formatted = dimensions?.[0]
        ? formatDimension(dimensions[0], value)
        : value;
      return truncate(`${formatted}`, DIMENSION_TRUNCATE);
    },
    [dimensions],
  );

  const renderLines = useCallback(
    (data: TChartData[]) => {
      if (!data.length) {
        return null;
      }

      const styles = chartModel.getLinesStyles(
        data,
        colourMap,
        metricsOrder,
        true,
        fills,
        layout,
      );

      return styles.map((style, i) => {
        const { key, id, stackId, fill, lineType, radius, cells, isPrevious } =
          style;

        switch (lineType as ApiYAxisChartType) {
          case ApiYAxisChartType.BAR:
            return (
              <Bar
                yAxisId={id}
                key={i}
                dataKey={key}
                fill={fill}
                radius={radius}
                stackId={stackId}
                isAnimationActive={!noAnimation}
              >
                {cells.map(({ key, fill, stroke }, index) => (
                  <Cell key={key} fill={fill} strokeWidth={2} stroke={stroke} />
                ))}
              </Bar>
            );
          case ApiYAxisChartType.AREA:
            return (
              <Area
                yAxisId={id}
                key={i}
                dataKey={key}
                fill={fill}
                stroke={fill}
                strokeWidth={2}
                fillOpacity={0.6}
                dot={false}
                stackId={stackId}
                isAnimationActive={!noAnimation}
              >
                {cells.map(({ key, fill, stroke }, index) => (
                  <Cell key={key} fill={fill} />
                ))}
              </Area>
            );

          case ApiYAxisChartType.DOT:
            return (
              <Line
                yAxisId={id}
                key={i}
                dataKey={key}
                strokeWidth={0}
                stroke={fill}
                dot={{ stroke: "white", fill, strokeWidth: 2, r: 5 }}
                activeDot={{
                  stroke: fill,
                  fill: "white",
                  strokeWidth: 2,
                  r: 5,
                }}
                isAnimationActive={false}
                type="monotoneX"
              >
                {cells.map(({ key, fill, stroke }, index) => (
                  <Cell key={key} fill={fill} />
                ))}
              </Line>
            );
          default:
            return (
              <Line
                yAxisId={id}
                key={i}
                dataKey={key}
                stroke={fill}
                strokeWidth={3}
                dot={false}
                isAnimationActive={false}
                type="monotoneX"
                strokeDasharray={isPrevious ? "3 3" : undefined}
              >
                {cells.map(({ key, fill, stroke }, index) => (
                  <Cell key={key} fill={fill} />
                ))}
              </Line>
            );
        }
      });
    },
    [colourMap, chartModel, fills, layout, metricsOrder, noAnimation],
  );

  const renderYAxis = useCallback(() => {
    if (!data.length) {
      return null;
    }

    if (!yAxisKeys) {
      return (
        <YAxis
          {...yStandardProps}
          {...(yAxisProps ? yAxisProps[0] : {})}
          allowDataOverflow={
            isVertical ? false : yStandardProps.allowDataOverflow
          }
          tickLine={
            isVertical
              ? {
                  stroke: theme.palette.primary.dark,
                }
              : undefined
          }
          dataKey={isVertical ? "name" : undefined}
          type={isVertical ? "category" : "number"}
          stroke={theme.palette.text.primary}
          axisLine={{ stroke: theme.palette.primary.dark, strokeWidth: 2 }}
          domain={
            isVertical
              ? undefined
              : yAxisProps && yAxisProps[0].domain
                ? yAxisProps[0].domain
                : ["dataMin", "dataMax"]
          }
          scale={isVertical ? "auto" : "linear"}
          width={Math.min(yWidths[0] || 40, isVertical ? 160 : 240)}
          tickFormatter={
            yAxisProps && yAxisProps[0].tickFormatter
              ? yAxisProps[0].tickFormatter
              : isVertical
                ? formatCategoryTick
                : (v) => {
                    return formatNumber(v) as string;
                  }
          }
        />
      );
    }
    return yAxisKeys.map((y, i) => {
      const k = typeof y === "object" ? y.key : y;

      return (
        <YAxis
          key={k}
          orientation={i === 0 ? "left" : "right"}
          yAxisId={`${k}-y`}
          {...yStandardProps}
          {...(yAxisProps ? yAxisProps[i] : {})}
          allowDataOverflow={
            isVertical ? false : yStandardProps.allowDataOverflow
          }
          tickLine={
            isVertical
              ? {
                  stroke: theme.palette.primary.dark,
                }
              : undefined
          }
          dataKey={isVertical ? "name" : undefined}
          type={isVertical ? "category" : "number"}
          axisLine={{ stroke: theme.palette.primary.dark, strokeWidth: 2 }}
          interval={"preserveStart"}
          domain={
            isVertical
              ? undefined
              : yAxisProps && yAxisProps[i] && yAxisProps[i].domain
                ? yAxisProps[i].domain
                : numericDomain
          }
          scale={isVertical ? "auto" : "linear"}
          width={Math.min(yWidths[i] || 40, isVertical ? 160 : 240)}
          tickFormatter={
            yAxisProps && yAxisProps[i] && yAxisProps[i].tickFormatter
              ? yAxisProps[i].tickFormatter
              : (v, _tickIndex) => {
                  if (isVertical) {
                    return formatCategoryTick(`${v}`);
                  }

                  const metric =
                    typeof y === "object" ? `${(y.fields || [])[0]}` : "";
                  return formatMetric(metric.split("\\").pop() as ApiMetric, v);
                }
          }
        />
      );
    });
  }, [
    data,
    theme,
    yAxisProps,
    yAxisKeys,
    yWidths,
    isVertical,
    formatCategoryTick,
    formatMetric,
    numericDomain,
  ]);

  const mergedData = useMemo(() => {
    if (!comparismentChart?.data) {
      return data.map(displayDataMapFn);
    }
    return mergeComparisonData(data, comparismentChart?.data).map(
      displayDataMapFn,
    );
  }, [data, comparismentChart?.data]);

  const total = useMemo(() => {
    return mergedData.reduce((agg, d) => {
      const { name, ...rest } = d;
      let t = 0;
      for (const key in rest) {
        t = t + Number(rest[key]);
      }
      return agg + t;
    }, 0);
  }, [mergedData]);

  if (total === 0) {
    return <NoData />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        position: "relative",
        "& .recharts-wrapper": {
          position: "absolute!important", // hack fix for revharts not resizing
        },
      }}
    >
      <ResponsiveContainer
        height={height ? height : aspect ? undefined : "100%"}
        width={"100%"}
        aspect={aspect}
        debounce={1}
      >
        <ComposedChart
          data={mergedData}
          barGap={6}
          maxBarSize={45}
          layout={layout}
          margin={{
            top: 5,
            right: hasRightAxis ? 0 : 5,
            bottom: 5,
            left: 0,
          }}
        >
          {!noAxis && (
            <CartesianGrid
              vertical={layout === "vertical"}
              horizontal={layout !== "vertical"}
              strokeDasharray="6"
              stroke={alpha(theme.palette.info.main, 0.5)}
            />
          )}

          {children}
          {renderLines(mergedData)}
          {showReferenceLineKey && data.length > 0 && (
            <ReferenceLine
              yAxisId={`${showReferenceLineKey}-y`}
              y={0}
              stroke={theme.palette.text.primary}
              strokeWidth={2}
            />
          )}
          {verticalReferenceLines.map((line, i) => (
            <ReferenceLine
              key={`vref-${line.x}-${i}`}
              yAxisId={`${(yAxisKeys?.[0] as ApiChartYAxisKey)?.key}-y`}
              x={line.x}
              stroke={line.stroke ?? alpha(theme.palette.text.secondary, 0.6)}
              strokeWidth={1}
              strokeDasharray="4 4"
              label={
                line.label == null
                  ? undefined
                  : typeof line.label === "string"
                    ? {
                        value: line.label,
                        position: "top" as const,
                        offset: 9,
                        fill: line.stroke ?? theme.palette.text.secondary,
                      }
                    : (() => {
                        const iconSize = 20;
                        const paddingBottom = 0;
                        const offset = 9;
                        return (
                          <Label
                            content={(props: any) => {
                              const vb = props.viewBox;
                              if (!vb || vb.x == null || vb.y == null)
                                return line.label;
                              const vw = vb.width ?? 0;
                              const vh = vb.height ?? 0;
                              const x = vb.x + vw / 2;
                              const y = vb.y - (vh >= 0 ? 1 : -1) * offset;
                              const contentEl =
                                line.label as React.ReactElement<{
                                  style?: React.CSSProperties;
                                }>;
                              return (
                                <g
                                  transform={`translate(${x}, ${y})`}
                                  textAnchor="middle"
                                >
                                  <foreignObject
                                    x={-iconSize / 2}
                                    y={-iconSize - paddingBottom}
                                    width={iconSize}
                                    height={iconSize + paddingBottom}
                                    style={{ overflow: "visible" }}
                                  >
                                    {React.cloneElement(contentEl, {
                                      style: {
                                        fontSize: iconSize,
                                        width: iconSize,
                                        height: iconSize,
                                        fill:
                                          line.stroke ??
                                          theme.palette.text.secondary,
                                        ...(typeof contentEl.props?.style ===
                                        "object"
                                          ? contentEl.props.style
                                          : {}),
                                      },
                                    })}
                                  </foreignObject>
                                </g>
                              );
                            }}
                            position="top"
                            offset={offset}
                            fill={line.stroke ?? theme.palette.text.secondary}
                          />
                        );
                      })()
              }
            />
          ))}
          {showTooltip && (
            <Tooltip
              cursor={{
                strokeDasharray: "3 3",
                stroke: theme.palette.info.main,
              }}
              content={
                <AnalyticsCardCustomTooltip
                  fills={fills}
                  metricsOrder={metricsOrder}
                  {...tooltipProps}
                />
              }
            />
          )}
          {!noLegend && (
            <Legend
              verticalAlign="top"
              align="left"
              wrapperStyle={{
                width: "auto",
              }}
              content={({ payload }) => (
                <ChartLegendWrapper colourMap={colourMap} payload={payload} />
              )}
            />
          )}

          {!noAxis && (
            <XAxis
              axisLine={{ stroke: theme.palette.primary.dark, strokeWidth: 2 }}
              interval={"equidistantPreserveStart"}
              height={isVertical ? Math.max(xHeight, 28) : xHeight}
              {...xProps}
              dataKey={isVertical ? undefined : "name"}
              type={isVertical ? "number" : "category"}
              domain={
                isVertical
                  ? (xAxisProps?.domain ?? numericDomain)
                  : xAxisProps?.domain
              }
              tickLine={
                !isVertical
                  ? {
                      stroke: theme.palette.primary.dark,
                    }
                  : undefined
              }
              tickFormatter={(v, k) => {
                if (isVertical) {
                  return formatMetric(
                    chartDataKeys[0].split("\\").pop() as ApiMetric,
                    v,
                  );
                }

                if (xAxisProps?.tickFormatter) {
                  return xAxisProps?.tickFormatter(v, k);
                }

                if (dimensions) {
                  return formatDimension(
                    dimensions[0],
                    truncate(v, DIMENSION_TRUNCATE),
                  );
                }
                return truncate(v, DIMENSION_TRUNCATE);
              }}
            />
          )}
          {!noAxis && renderYAxis()}
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default memo(StyledComposedChart);
